import {
  ACTION_ARGS,
  Arg,
  COMPONENT_STATE_ARGS,
  Metadata,
  pick,
} from '../../../utils/spds/metadata';
import { eq } from 'ember-truth-helpers';
import { modifier } from 'ember-modifier';
import { tracked } from '@glimmer/tracking';
import { waitForRender } from '../../../utils/waiters';
import SegmentedControl from '../action/segmented-control.gjs';
import Super from '../core/base.gjs';
import styles from './tabs.module.scss';

const METADATA = Metadata.define({
  description: 'Tab navigation component that wraps segmented control for switching between views',
  extends: [
    pick(ACTION_ARGS, [{ name: 'onClick', required: true }]),
    pick(COMPONENT_STATE_ARGS, ['disabled']),
  ],
  meta: {
    activeTab: Arg.string({ description: 'The currently active tab name' }),
    tabs: Arg.array({ description: 'Array of tab names to display' }).required(),
  },
  name: 'Tabs',
});

export default class SpdsNavigationTabs extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);
  baseClass = 'spds-navigation-tabs';
  styles = styles;

  @tracked _activeTab;
  @tracked _previousActiveTabArg;

  get activeTab() {
    return this._activeTab;
  }

  set activeTab(value) {
    if (this.args.tabs.includes(value)) {
      this._activeTab = value;
    }
  }

  get activeTabArgDidChange() {
    return this.args.activeTab !== this.previousActiveTabArg;
  }

  get previousActiveTabArg() {
    return this._previousActiveTabArg;
  }

  set previousActiveTabArg(value) {
    this._previousActiveTabArg = value;
  }

  get shouldUpdateTabFromArg() {
    return this.args.activeTab && this.activeTabArgDidChange;
  }

  updateActiveTabFromArg = modifier(async () => {
    let { activeTab } = this.args;

    if (activeTab && this.shouldUpdateTabFromArg) {
      await waitForRender();

      this.activeTab = activeTab;
      this.previousActiveTabArg = activeTab;
    }
  });

  onClick = evt => {
    // Segmented-control radios use Spds::Core::Input, which invokes @onClick with a payload object
    // `{ value, event, ... }` (no `evt.target`). Raw DOM clicks still expose `evt.target.value`.
    let value = evt?.target?.value ?? evt?.value;

    this.activeTab = value;
    this.args.onClick(value);
  };

  setup = () => {
    this.activeTab = this.args.activeTab ?? this.args.tabs[0];
    this.previousActiveTabArg = this.args.activeTab;
  };

  <template>
    <SegmentedControl
      @componentName={{this.componentName}}
      @disabled={{@disabled}}
      @id={{this.id}}
      @onClick={{this.onClick}}
      class={{this.classes}}
      data-test-spds-navigation-tabs
      {{this.setupComponent}}
      {{this.updateActiveTabFromArg}}
      {{this.validateArguments}}
      ...attributes
      as |sc|
    >
      {{#each @tabs as |tabName|}}
        <sc.Button
          checked={{eq tabName this.activeTab}}
          class="spds-navigation-tab"
          data-test-spds-navigation-tab={{tabName}}
          value={{tabName}}
        >
          {{tabName}}
        </sc.Button>
      {{/each}}
    </SegmentedControl>
  </template>
}

export const NAVIGATION_TABS_METADATA = SpdsNavigationTabs.metadata;
